/**
 * Vertical discontinuity (Amt = 90) through the centre of a vertical shaft
 * (Shaft_Special Case_Vertical Discontinuity_Algorithm_Rev.1_17Oct26).
 *
 * The plane cuts the shaft wall along two vertical lines, at the two strike
 * directions. On the unfolded log, which spans 180° either side of the View
 * Direction, each line sits at B degrees clockwise from D: right of the
 * centreline for 0 < B < 180, left for 180 < B < 360.
 */

export const VIEW_AXIS_ALERT =
  "Discontinuity traces coincide with the View Direction axis and its opposite axis. " +
  "The opposite-axis trace is represented at both edges of the unfolded shaft log.";

export const PERPENDICULAR_AXIS_ALERT =
  "Discontinuity traces coincide with the two axes perpendicular to the View Direction axis.";

const mod360 = (a) => ((a % 360) + 360) % 360;

export const isVerticalJoint = (amt) => parseFloat(amt) === 90;

/**
 * Positions of the two traces for view direction D, dip direction Dir and
 * shaft radius R (metres).
 *
 * Each trace is { b, side, distance } where distance is the circumferential
 * distance from the centreline in metres and side is "centre", "right",
 * "left" or "edges" (B = 180: drawn at both edges of the log).
 */
export function verticalJointTraces({ D, Dir, R }) {
  const S1 = mod360(Number(Dir) + 90);
  const B1 = mod360(S1 - Number(D));
  const B2 = mod360(B1 + 180);
  const HC = Math.PI * Number(R);

  const place = (b) => {
    if (b === 0) return { b, side: "centre", distance: 0 };
    if (b === 180) return { b, side: "edges", distance: HC };
    if (b < 180) return { b, side: "right", distance: (HC * b) / 180 };
    return { b, side: "left", distance: (HC * (360 - b)) / 180 };
  };

  let alert = null;
  if (B1 === 0 || B1 === 180) alert = VIEW_AXIS_ALERT;
  else if (B1 === 90 || B1 === 270) alert = PERPENDICULAR_AXIS_ALERT;

  return { traces: [place(B1), place(B2)], alert };
}

/**
 * Signed distance from the centreline in metres, positive to the right.
 * "edges" yields both edges.
 */
export const signedDistances = (trace) => {
  if (trace.side === "edges") return [-trace.distance, trace.distance];
  if (trace.side === "left") return [-trace.distance];
  return [trace.distance];
};
